import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, User, Phone, Calendar, DollarSign, Edit, Plus, FileText } from "lucide-react";
import SyncIndicator from "@/components/SyncIndicator";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { syncService } from "../services/SyncService";
import { MobileHeader } from "@/components/MobileHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PatientNotesTimeline from "@/components/notes/PatientNotesTimeline";
import PatientEditDialog from "@/components/PatientEditDialog";
import { FeeAgreementManager } from "@/components/FeeAgreementManager";
import { formatRupees, toPaise } from "../utils/currency";
import { useUserRole } from "../hooks/useUserRole";
import PropTypes from 'prop-types';
import { getDisplayAge, formatDOB } from "../utils/patient";
import { useMode } from "@/context/ModeContext";
import { getScopedPatientAppointments } from "../utils/patientDetailAppointments";
import { getAppointmentAccess } from "../utils/accessPolicy";

const patientTabClassName = "shrink-0 gap-2 rounded-lg px-4 py-2 text-base font-semibold text-slate-500 shadow-none data-[state=active]:bg-sky-500 data-[state=active]:text-white data-[state=active]:shadow-none";

export default function PatientDetail({ token, onLogout }) {
  const { isLoading, isReceptionistOnly, memberships } = useUserRole();
  const { activeMode } = useMode();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showChargeDialog, setShowChargeDialog] = useState(false);
  const [chargeFormData, setChargeFormData] = useState({
    appointment_id: "",
    charge_amount: ""
  });

  const doctorId = localStorage.getItem('user_id');

  const data = useLiveQuery(async () => {
    const patient = await db.patients.get(patientId);
    if (!patient) return null;

    const appointments = await db.appointments.where('patient_id').equals(patientId).toArray();
    const aptIds = appointments.map(a => a.id);
    const facility_charges = await db.facility_charges.where('appointment_id').anyOf(aptIds).toArray();

    // Get referral doctor info
    let referralDoctor = null;
    if (patient.referral_doctor_id) {
      referralDoctor = await db.users.get(patient.referral_doctor_id);
    }

    return { patient, appointments, facility_charges, referralDoctor };
  }, [patientId]);

  const rawAppointments = data?.appointments ?? [];
  const rawFacilityCharges = data?.facility_charges ?? [];
  const scopedAppointments = useMemo(
    () => getScopedPatientAppointments(rawAppointments, activeMode, doctorId),
    [rawAppointments, activeMode, doctorId]
  );
  const scopedAppointmentIds = useMemo(
    () => new Set(scopedAppointments.map(appointment => appointment.id)),
    [scopedAppointments]
  );
  const appointmentAccess = useMemo(
    () => new Map(scopedAppointments.map(appointment => [
      appointment.id,
      getAppointmentAccess(appointment, doctorId, memberships),
    ])),
    [scopedAppointments, doctorId, memberships]
  );
  const scopedFacilityCharges = useMemo(
    () => rawFacilityCharges.filter(charge => {
      if (!scopedAppointmentIds.has(charge.appointment_id)) return false;
      const access = appointmentAccess.get(charge.appointment_id);
      return access?.canViewFee;
    }),
    [rawFacilityCharges, scopedAppointmentIds, appointmentAccess]
  );

  // Find appointments without facility_charges (for manual facility_charge creation)
  const appointmentsWithoutCharge = useMemo(() => {
    const chargeAptIds = new Set(scopedFacilityCharges.map(c => c.appointment_id));
    return scopedAppointments.filter(apt => {
      const access = appointmentAccess.get(apt.id);
      return (
        access?.canEdit &&
        !chargeAptIds.has(apt.id) &&
        (apt.status === 'completed' || apt.status === 'cancelled')
      );
    });
  }, [scopedAppointments, scopedFacilityCharges, appointmentAccess]);

  // Calculate suggested facility_charge - must be before early return
  const suggestedCommission = useMemo(() => {
    if (!data) return null;
    const selectedAppointment = scopedAppointments.find(a => a.id === chargeFormData.appointment_id);
    if (!selectedAppointment || !data.patient?.rule_type || !data.patient?.fee_value) return null;
    // suggestedCommission now returns paise
    if (data.patient.rule_type === 'percent') {
      // fee_amount is paise, fee_value is whole percentage
      return selectedAppointment.fee_amount * data.patient.fee_value / 100;
    }
    // flat facility_charge, fee_value is paise
    return data.patient.fee_value;
  }, [data, scopedAppointments, chargeFormData.appointment_id]);

  if (!data) {
    return (
      <div className="page-container">
        <Sidebar active="patients" onLogout={onLogout} />
        <div className="main-content flex items-center justify-center min-h-[400px] text-slate-400">
          Loading patient...
        </div>
      </div>
    );
  }

  const { patient } = data;



  const handleAddCharge = async (e) => {
    e.preventDefault();
    try {
      const appointment = scopedAppointments.find(a => a.id === chargeFormData.appointment_id);
      if (!appointment) {
        toast.error("Please select an appointment");
        return;
      }

      const access = getAppointmentAccess(appointment, doctorId, memberships);
      if (!access.canEdit) {
        toast.error("You do not have permission to add facility charge for this appointment");
        return;
      }

      let commissionAmount = parseInt(chargeFormData.charge_amount);

      // Auto-calculate if no amount provided and patient has facility_charge settings
      if (!commissionAmount && patient.referral_doctor_id && patient.rule_type && patient.fee_value) {
        if (patient.rule_type === 'percent') {
          // fee_amount is paise, fee_value is whole percentage
          commissionAmount = appointment.fee_amount * patient.fee_value / 100;
        } else {
          // flat, already in paise
          commissionAmount = patient.fee_value;
        }
      } else if (commissionAmount) {
        // User entered amount in rupees, convert to paise
        commissionAmount = toPaise(chargeFormData.charge_amount);
      }

      if (!commissionAmount || commissionAmount <= 0) {
        toast.error("Please enter a valid facility_charge amount");
        return;
      }

      const newCharge = {
        id: `comm_${appointment.id}`,
        appointment_id: appointment.id,
        associated_doctor_id: appointment.doctor_id || doctorId, // The doctor being charged
        clinic_id: appointment.clinic_id || null,
        charge_amount: commissionAmount,
        paid_status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        _synced: false
      };

      await db.facility_charges.add(newCharge);
      await syncService.addToQueue('create', 'facility_charge', newCharge);

      toast.success("Facility charge created successfully!");
      setShowChargeDialog(false);
      setChargeFormData({ appointment_id: "", charge_amount: "" });
    } catch (error) {
      console.error(error);
      toast.error("Failed to create facility_charge");
    }
  };


  return (
    <div className="page-container" data-testid="patient-detail-page">
      <Sidebar active="patients" onLogout={onLogout} />
      <div className="main-content">
        {/* Desktop Header */}
        <div className="header hidden md:flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            data-testid="back-button"
            onClick={() => navigate("/patients")}
            className="flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Back to Patients
          </Button>

          <div className="flex gap-2">
            <Button
              variant="outline"
              data-testid="edit-patient-button"
              onClick={() => setShowEditDialog(true)}
              className="flex items-center gap-2"
            >
              <Edit size={18} />
              Edit Patient
            </Button>
          </div>
        </div>

        {/* Mobile Header */}
        <MobileHeader title="Patient Details">
          <div className="flex gap-2 w-full mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEditDialog(true)}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Edit size={16} /> Edit
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/patients")}
            className="w-full flex items-center justify-start gap-2 text-slate-500 -ml-2"
          >
            <ArrowLeft size={16} /> Back to List
          </Button>
        </MobileHeader>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="flex h-auto w-full max-w-full flex-nowrap justify-start gap-2 overflow-x-auto rounded-none border-b border-slate-200 bg-transparent p-0 pb-2 text-slate-500 [-webkit-overflow-scrolling:touch]">
            <TabsTrigger value="overview" className={patientTabClassName}>
              <User size={18} aria-hidden="true" />
              Overview
            </TabsTrigger>
            {!isLoading && !isReceptionistOnly && (
              <TabsTrigger value="clinical-notes" className={patientTabClassName}>
                <FileText size={18} aria-hidden="true" />
                Clinical Notes
              </TabsTrigger>
            )}
            <TabsTrigger value="appointments" className={patientTabClassName}>
              <Calendar size={18} aria-hidden="true" />
              Appointments
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
        <div className="card p-4 md:p-6 mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-8">
            <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <User size={40} className="text-blue-800" />
            </div>
            <div className="flex-1 w-full text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-bold mb-2 text-slate-900" data-testid="patient-name">{patient?.name}</h1>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mt-4 text-left">
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <div className="text-slate-500 text-sm mb-1">Age</div>
                  <div className="font-semibold">{getDisplayAge(patient) ? `${getDisplayAge(patient)} years` : "—"}</div>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <div className="text-slate-500 text-sm mb-1">Gender</div>
                  <div className="font-semibold capitalize">{patient?.gender}</div>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <div className="text-slate-500 text-sm mb-1 flex items-center gap-1"><Phone size={14} />Phone</div>
                  <div className="font-semibold break-all">{patient?.phone}</div>
                </div>
                {patient?.date_of_birth && (
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                    <div className="text-slate-500 text-sm mb-1">Date of Birth</div>
                    <div className="font-semibold">{formatDOB(patient.date_of_birth)}</div>
                  </div>
                )}
                {patient?.email && (
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 sm:col-span-3">
                    <div className="text-slate-500 text-sm mb-1">Email</div>
                    <div className="font-semibold break-all">{patient?.email}</div>
                  </div>
                )}
                {patient?.address && (
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 sm:col-span-3">
                    <div className="text-slate-500 text-sm mb-1">Address</div>
                    <div className="font-semibold break-all">{patient?.address}</div>
                  </div>
                )}
              </div>

              {patient?.notes && !isReceptionistOnly && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg text-left border border-slate-100">
                  <div className="text-slate-500 text-sm mb-1">Notes</div>
                  <div className="text-slate-700">{patient.notes}</div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6">
            {!isReceptionistOnly && <FeeAgreementManager userId={doctorId} patientId={patientId} />}
          </div>
        </div>

            {!isReceptionistOnly && (
              <div className="card p-0 overflow-hidden flex flex-col h-full bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div className="p-4 md:p-6 border-b border-slate-100 flex flex-wrap justify-between items-center gap-4">
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <DollarSign size={20} className="text-emerald-500" />
                    FacilityCharges
                  </h2>
                  {appointmentsWithoutCharge.length > 0 && patient.referral_doctor_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="add-facility_charge-button"
                      onClick={() => setShowChargeDialog(true)}
                      className="flex items-center gap-1"
                    >
                      <Plus size={16} />
                      Add
                    </Button>
                  )}
                </div>

                {scopedFacilityCharges.length === 0 ? (
                  <div className="text-center p-8 text-slate-400 flex-1 flex items-center justify-center">No facility_charges</div>
                ) : (
                  <div className="flex flex-col gap-4 p-4 md:p-6 bg-slate-50">
                    {scopedFacilityCharges.map((comm) => (
                      <div
                        key={comm.id}
                        className={`relative p-4 md:p-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer ${comm._synced === false ? 'bg-orange-50 hover:bg-orange-100' : ''}`}
                        onClick={() => navigate(`/facility-charges/${comm.id}`)}
                      >
                        {comm._synced === false && (
                          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-orange-400 rounded-l-2xl" />
                        )}
                        {comm._synced !== false && (
                          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-emerald-500 rounded-l-2xl" />
                        )}

                        <div className="flex justify-between items-start mb-2 gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900">{formatRupees(comm.charge_amount)}</span>
                            <SyncIndicator show={comm._synced === false} />
                          </div>
                          <span className={`badge badge-${comm.paid_status} shrink-0`}>{comm.paid_status}</span>
                        </div>

                        {comm.payment_date && (
                          <div className="text-sm text-slate-500 mt-1">
                            Paid: {new Date(comm.payment_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          {!isLoading && !isReceptionistOnly && (
            <TabsContent value="clinical-notes">
              <PatientNotesTimeline
                appointments={scopedAppointments}
                userId={doctorId}
                token={token}
                onScheduleAppointment={() => navigate("/appointments")}
              />
            </TabsContent>
          )}
          <TabsContent value="appointments">
          <div className="card p-0 overflow-hidden flex flex-col h-full bg-white rounded-2xl border border-slate-200 shadow-sm">
            <div className="p-4 md:p-6 border-b border-slate-100">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Calendar size={20} className="text-slate-400" />
                Appointment History
              </h2>
            </div>
            {scopedAppointments.length === 0 ? (
              <div className="text-center p-8 text-slate-400 flex-1 flex items-center justify-center">No appointments yet</div>
            ) : (
              <div className="flex flex-col gap-4 p-4 md:p-6 bg-slate-50">
                {scopedAppointments.map((apt) => (
                  <div
                    key={apt.id}
                    className={`relative p-4 md:p-6 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all ${apt._synced === false ? 'bg-orange-50 hover:bg-orange-100' : ''}`}
                  >
                    {apt._synced === false && (
                      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-orange-400 rounded-l-2xl" />
                    )}
                    {apt._synced !== false && (
                      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-sky-500 rounded-l-2xl" />
                    )}

                    <div
                      className="cursor-pointer"
                      onClick={() => navigate(`/appointments/${apt.id}`)}
                    >
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{new Date(apt.date_time).toLocaleDateString()}</span>
                            <span className="text-slate-500 text-sm">{new Date(apt.date_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <SyncIndicator show={apt._synced === false} />
                        </div>
                        <span className={`badge badge-${apt.status} shrink-0`}>{apt.status}</span>
                      </div>

                      {appointmentAccess.get(apt.id)?.canViewFee && (
                        <div className="text-xl font-bold text-sky-500 mb-2">
                          Fee: {formatRupees(apt.fee_amount)}
                        </div>
                      )}

                      {apt.notes && (
                        <div className="text-sm text-slate-500 line-clamp-2">{apt.notes}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Patient Dialog */}
      <PatientEditDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        patient={patient}
        userId={doctorId}
      />

      {/* Add Facility Charge Dialog */}
      <Dialog open={showChargeDialog} onOpenChange={setShowChargeDialog}>
        <DialogContent data-testid="add-facility_charge-dialog">
          <DialogHeader>
            <DialogTitle>Add Facility Charge</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCharge}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <Label htmlFor="appointment">Select Appointment</Label>
                <Select
                  value={chargeFormData.appointment_id}
                  onValueChange={(value) => setChargeFormData({ ...chargeFormData, appointment_id: value })}
                >
                  <SelectTrigger data-testid="facility_charge-appointment-select">
                    <SelectValue placeholder="Select an appointment" />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentsWithoutCharge.map((apt) => (
                      <SelectItem key={apt.id} value={apt.id}>
                        {new Date(apt.date_time).toLocaleDateString()} - {formatRupees(apt.fee_amount)} ({apt.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="charge_amount">Facility Charge Amount (₹) - Whole rupees</Label>
                <Input
                  id="charge_amount"
                  data-testid="facility_charge-amount-input"
                  type="number"
                  step="1"
                  min="0"
                  placeholder={suggestedCommission ? `Suggested: ${formatRupees(suggestedCommission)}` : "Amount in Rupees"}
                  value={chargeFormData.charge_amount}
                  onChange={(e) => setChargeFormData({ ...chargeFormData, charge_amount: e.target.value })}
                />
                {suggestedCommission && !chargeFormData.charge_amount && (
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                    Leave empty to use suggested amount based on patient&apos;s facility_charge settings
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
              <Button type="button" variant="outline" onClick={() => setShowChargeDialog(false)}>
                Cancel
              </Button>
              <Button
                data-testid="submit-facility_charge-button"
                type="submit"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                Add Facility Charge
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div >
  );
}

PatientDetail.propTypes = {
  token: PropTypes.string,
  onLogout: PropTypes.func
};
